import React, { useCallback, useEffect, useState } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/src/components/ui/drawer";
import useTourneyLobby from "@/src/hooks/utils/useTourneyLobby";
import {
  SORTING_TIME_ASC,
  SORTING_TIME_DESC,
  SORTING_REGISTRATION_ASC,
  SORTING_REGISTRATION_DESC,
  SORTING_PRIZE_ASC,
  SORTING_PRIZE_DESC,
} from "@/Poker/data/Constants";
import { SheetClose } from "../ui/sheet";
import { IoCloseSharp } from "react-icons/io5";
import useTourneyFilterSortConfig from "@/src/hooks/data/useTourneyFilterSortConfig";

const A23TourneyGamesSort = ({ removeFilter, setRemoveFilter }) => {
  const [gameType, setGameType] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [tourneyStatus, setTourneyStatus] = useState([]);
  const [buyIn, setBuyIn] = useState([]);
  const [format, setFormat] = useState([]);
  const [isOpen, setIsOpen] = useState(true);
  const tourneylobby = useTourneyLobby();

  const currentFilters = useTourneyFilterSortConfig();

  useEffect(() => {
    if (currentFilters) {
      setSortBy(currentFilters.sortBy || "");
      setGameType(currentFilters.gameType || []);
      setTourneyStatus(currentFilters.tourneyStatus || []);
      setBuyIn(currentFilters.buyIn || []);
      setFormat(currentFilters.format || []);
    }
  }, [
    currentFilters.sortBy,
    currentFilters.gameType,
    currentFilters.tourneyStatus,
    currentFilters.buyIn,
    currentFilters.format,
  ]);

  const resetFilters = useCallback(() => {
    setSortBy("");
    setGameType([]);
    setTourneyStatus([]);
    setBuyIn([]);
    setFormat([]);

    const resetPayload = {
      hasActiveFilters: false,
      gameType: [],
      tourneyStatus: [],
      buyIn: [],
      format: [],
      sortBy: "",
    };

    tourneylobby?.requestTourneyFilterSortConfig(resetPayload);
    setIsOpen(false);
  }, [tourneylobby]);

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return (
      a.every((val) => b.includes(val)) && b.every((val) => a.includes(val))
    );
  };

  const hasChanges =
    sortBy !== (currentFilters.sortBy || "") ||
    !arraysEqual(gameType, currentFilters.gameType || []) ||
    !arraysEqual(tourneyStatus, currentFilters.tourneyStatus || []) ||
    !arraysEqual(buyIn, currentFilters.buyIn || []) ||
    !arraysEqual(format, currentFilters.format || []);

  const hasFiltersSelected =
    sortBy.length > 0 ||
    gameType.length > 0 ||
    tourneyStatus.length > 0 ||
    buyIn.length > 0 ||
    format.length > 0;

  const shouldEnableApply = hasChanges;

  const applyFilters = useCallback(() => {
    let filter = {
      hasActiveFilters: hasFiltersSelected,
      gameType,
      tourneyStatus,
      buyIn,
      format,
      sortBy,
    };
    tourneylobby?.requestTourneyFilterSortConfig(filter);
    setIsOpen(false);
  }, [
    sortBy,
    gameType,
    tourneyStatus,
    buyIn,
    format,
    hasFiltersSelected,
    tourneylobby,
  ]);

  const handleSortByChange = (value) => {
    setSortBy(value);
  };

  const handleMultipleSelection = (value, currentState, setState) => {
    if (currentState.includes(value)) {
      setState(currentState.filter((item) => item !== value));
    } else {
      setState([...currentState, value]);
    }
  };

  useEffect(() => {
    if (removeFilter) {
      resetFilters();
      setRemoveFilter(true);
    }
    setRemoveFilter(false);
  }, [removeFilter]);

  return (
    <Drawer>
      <DrawerTrigger>
        <div className="w-8 h-8 flex items-center justify-center bg-header-fg border rounded-[5px] border-b-sub overflow-hidden">
          {!currentFilters.hasActiveFilters ? (
            <svg
              width="20"
              height="16"
              viewBox="0 0 20 16"
              className=""
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.1429 2.28574H16.4C16.2181 2.28574 16.0437 2.21349 15.9151 2.08489C15.7865 1.9563 15.7143 1.78188 15.7143 1.60002C15.7143 1.41816 15.7865 1.24374 15.9151 1.11515C16.0437 0.986551 16.2181 0.914307 16.4 0.914307H19.1429C19.3247 0.914307 19.4991 0.986551 19.6277 1.11515C19.7563 1.24374 19.8286 1.41816 19.8286 1.60002C19.8286 1.78188 19.7563 1.9563 19.6277 2.08489C19.4991 2.21349 19.3247 2.28574 19.1429 2.28574Z"
                className="fill-h-secondary"
              />
              <path
                d="M19.1428 5.94284H15.4857C15.3038 5.94284 15.1294 5.87059 15.0008 5.742C14.8722 5.6134 14.8 5.43899 14.8 5.25713C14.8 5.07526 14.8722 4.90085 15.0008 4.77225C15.1294 4.64366 15.3038 4.57141 15.4857 4.57141H19.1428C19.3247 4.57141 19.4991 4.64366 19.6277 4.77225C19.7563 4.90085 19.8286 5.07526 19.8286 5.25713C19.8286 5.43899 19.7563 5.6134 19.6277 5.742C19.4991 5.87059 19.3247 5.94284 19.1428 5.94284Z"
                className="fill-h-secondary"
              />
              <path
                d="M19.1429 9.59994H13.6571C13.4753 9.59994 13.3009 9.5277 13.1723 9.3991C13.0437 9.27051 12.9714 9.09609 12.9714 8.91423C12.9714 8.73237 13.0437 8.55795 13.1723 8.42936C13.3009 8.30076 13.4753 8.22852 13.6571 8.22852H19.1429C19.3247 8.22852 19.4991 8.30076 19.6277 8.42936C19.7563 8.55795 19.8286 8.73237 19.8286 8.91423C19.8286 9.09609 19.7563 9.27051 19.6277 9.3991C19.4991 9.5277 19.3247 9.59994 19.1429 9.59994Z"
                className="fill-h-secondary"
              />
              <path
                d="M19.1429 13.2572H14.5715C14.3896 13.2572 14.2152 13.1849 14.0866 13.0563C13.958 12.9277 13.8857 12.7533 13.8857 12.5715C13.8857 12.3896 13.958 12.2152 14.0866 12.0866C14.2152 11.958 14.3896 11.8857 14.5715 11.8857H19.1429C19.3247 11.8857 19.4992 11.958 19.6278 12.0866C19.7564 12.2152 19.8286 12.3896 19.8286 12.5715C19.8286 12.7533 19.7564 12.9277 19.6278 13.0563C19.4992 13.1849 19.3247 13.2572 19.1429 13.2572Z"
                className="fill-h-secondary"
              />
              <path
                d="M8.62859 16H5.88574C5.58272 15.9997 5.29219 15.8792 5.07792 15.665C4.86366 15.4507 4.74316 15.1602 4.74288 14.8571V9.13304L0.383949 3.03215C0.245215 2.83847 0.170877 2.60609 0.171451 2.36785V1.14286C0.171732 0.839839 0.29223 0.549312 0.506496 0.335045C0.720763 0.120779 1.01129 0.00028081 1.31431 0H13.2C13.503 0.00028081 13.7936 0.120779 14.0078 0.335045C14.2221 0.549312 14.3426 0.839839 14.3429 1.14286V2.36785C14.3435 2.60609 14.2691 2.83847 14.1304 3.03215L9.77145 9.13304V14.8571C9.77117 15.1602 9.65067 15.4507 9.43641 15.665C9.22214 15.8792 8.93161 15.9997 8.62859 16ZM6.11431 14.6286H8.40002V9.06072C8.39945 8.82248 8.47379 8.5901 8.61252 8.39643L12.9715 2.29553V1.37143H1.54288V2.29553L5.90181 8.39643C6.04054 8.5901 6.11488 8.82248 6.11431 9.06072V14.6286Z"
                className="fill-h-secondary"
              />
              <path
                d="M12.7428 13.2572C13.1216 13.2572 13.4286 12.9502 13.4286 12.5715C13.4286 12.1927 13.1216 11.8857 12.7428 11.8857C12.3641 11.8857 12.0571 12.1927 12.0571 12.5715C12.0571 12.9502 12.3641 13.2572 12.7428 13.2572Z"
                className="fill-h-secondary"
              />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_3266_115886)">
                <path
                  d="M9.00004 11.3016V17.0588C9.00004 17.3084 8.90088 17.5478 8.72437 17.7243C8.54787 17.9008 8.30848 18 8.05886 18H5.94121C5.6916 18 5.45221 17.9008 5.2757 17.7243C5.0992 17.5478 5.00004 17.3084 5.00004 17.0588V11.3016C5.00004 11.1055 4.93875 10.9142 4.82472 10.7546L0.175313 4.24542C0.0612907 4.0858 -3.173e-06 3.89454 1.23195e-10 3.69837V2.94118C1.23195e-10 2.69156 0.0991594 2.45217 0.275664 2.27566C0.452169 2.09916 0.691561 2 0.941176 2H13.0588C13.1824 2 13.3048 2.02434 13.419 2.07164C13.5332 2.11894 13.6369 2.18827 13.7243 2.27566C13.8117 2.36306 13.8811 2.46682 13.9284 2.581C13.9757 2.69519 14 2.81758 14 2.94118V3.69838C14 3.89455 13.9387 4.08581 13.8247 4.24543L9.17527 10.7546C9.06128 10.9142 9.00001 11.1055 9.00004 11.3016Z"
                  className="fill-icon-success"
                />
                <path
                  d="M16.4119 4.35293C16.1623 4.35293 15.9229 4.25377 15.7464 4.07727C15.5699 3.90076 15.4707 3.66137 15.4707 3.41176C15.4707 3.16214 15.5699 2.92275 15.7464 2.74625C15.9229 2.56974 16.1623 2.47058 16.4119 2.47058H19.2354C19.485 2.47058 19.7244 2.56974 19.9009 2.74625C20.0774 2.92275 20.1766 3.16214 20.1766 3.41176C20.1766 3.66137 20.0774 3.90076 19.9009 4.07727C19.7244 4.25377 19.485 4.35293 19.2354 4.35293H16.4119Z"
                  className="fill-icon-success"
                />
                <path
                  d="M15.4707 8.1177C15.2211 8.1177 14.9817 8.01854 14.8052 7.84204C14.6287 7.66554 14.5295 7.42614 14.5295 7.17653C14.5295 6.92691 14.6287 6.68752 14.8052 6.51102C14.9817 6.33451 15.2211 6.23535 15.4707 6.23535H19.2354C19.485 6.23535 19.7244 6.33451 19.9009 6.51102C20.0774 6.68752 20.1766 6.92691 20.1766 7.17653C20.1766 7.42614 20.0774 7.66554 19.9009 7.84204C19.7244 8.01854 19.485 8.1177 19.2354 8.1177H15.4707Z"
                  className="fill-icon-success"
                />
                <path
                  d="M13.5883 11.8824C13.3387 11.8824 13.0993 11.7832 12.9228 11.6067C12.7463 11.4302 12.6471 11.1908 12.6471 10.9412C12.6471 10.6916 12.7463 10.4522 12.9228 10.2757C13.0993 10.0992 13.3387 10 13.5883 10H19.2353C19.4849 10 19.7243 10.0992 19.9008 10.2757C20.0773 10.4522 20.1765 10.6916 20.1765 10.9412C20.1765 11.1908 20.0773 11.4302 19.9008 11.6067C19.7243 11.7832 19.4849 11.8824 19.2353 11.8824H13.5883Z"
                  className="fill-icon-success"
                />
                <path
                  d="M12.6471 15.647C12.3975 15.647 12.1581 15.5478 11.9816 15.3713C11.8051 15.1948 11.7059 14.9554 11.7059 14.7058C11.7059 14.4562 11.8051 14.2168 11.9816 14.0403C12.1581 13.8638 12.3975 13.7646 12.6471 13.7646H19.2353C19.485 13.7646 19.7244 13.8638 19.9009 14.0403C20.0774 14.2168 20.1765 14.4562 20.1765 14.7058C20.1765 14.9554 20.0774 15.1948 19.9009 15.3713C19.7244 15.5478 19.485 15.647 19.2353 15.647H12.6471Z"
                  className="fill-icon-success"
                />
              </g>
              <defs>
                <clipPath id="clip0_3266_115886">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          )}

        </div>
      </DrawerTrigger>

      <DrawerContent side="bottom">
        <div className="pb-3 text-primary rounded-300 bg-linear-to-b from-foreground to-background">
          <div className="flex relative justify-center py-4 border-b-1 border-b-main font-bold">
            <h2>Sort & Filter</h2>
            <SheetClose>
              <IoCloseSharp className="absolute right-4 top-5 size-4 text-primary" />
            </SheetClose>
          </div>

          <div className="px-6 py-2 overflow-scroll h-96">
            {/* Sort By Section */}
            <div className="mb-8 border-b-2 border-b-main pb-4">
              <div className="text-warning text-base font-semibold mb-4">
                Sort by:
              </div>

              {[
                { title: "Time : Starting Soon", sort: SORTING_TIME_ASC },
                { title: "Time : Starting Later", sort: SORTING_TIME_DESC },
                {
                  title: "Registration Fee : Low to High",
                  sort: SORTING_REGISTRATION_ASC,
                },
                {
                  title: "Registration Fee : High to Low",
                  sort: SORTING_REGISTRATION_DESC,
                },
                { title: "Prize : Low to High", sort: SORTING_PRIZE_ASC },
                { title: "Prize : High to Low", sort: SORTING_PRIZE_DESC },
              ].map(({ title, sort }) => (
                <div key={title} className="flex items-center mb-3">
                  <input
                    type="radio"
                    id={title}
                    name="sortBy"
                    checked={sortBy === sort}
                    onChange={() => handleSortByChange(sort)}
                    className="mr-3 w-[18px] h-[18px] rounded-900 accent-b-main border-secondary"
                  />
                  <label htmlFor={title} className="text-primary text-sm">
                    {title}
                  </label>
                </div>
              ))}
            </div>

            {/* Game Type Section */}
            <div className="mb-6">
              <div className="text-warning text-base font-semibold mb-4">
                Game Type:
              </div>

              <div className="flex gap-3 flex-wrap">
                {[
                  { label: "NLH", value: "TEXAS_HOLDEM" },
                  { label: "PLO4", value: "OMAHA" },
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() =>
                      handleMultipleSelection(value, gameType, setGameType)
                    }
                    className={`rounded-300 px-4 py-2 text-sm transition-all duration-300 ease-in-out ${
                      gameType.includes(value)
                        ? "bg-filter-active shadow-[2px_2px_15px] shadow-filter-shadow text-light border-1 border-b-sub"
                        : "bg-transparent text-secondary border-1 border-b-secondary"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* By Status Section */}
            <div className="mb-6">
              <div className="text-warning text-base font-semibold mb-4">
                By Status:
              </div>

              <div className="flex gap-3 flex-wrap">
                {[
                  { label: "Late Registration", value: "LATE_REGISTRATION" },
                  { label: "Registering", value: "REGISTRATION_STARTED" },
                  { label: "Running", value: "LIVE" },
                  { label: "Announced", value: "ANNOUNCED" },
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() =>
                      handleMultipleSelection(
                        value,
                        tourneyStatus,
                        setTourneyStatus
                      )
                    }
                    className={`rounded-300 px-4 py-2 text-sm transition-all duration-300 ease-in-out mb-2 ${
                      tourneyStatus.includes(value)
                        ? "bg-filter-active shadow-[2px_2px_15px] shadow-filter-shadow text-light border-1 border-b-sub"
                        : "bg-transparent text-secondary border-1 border-b-secondary"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Buy In Section */}
            <div className="mb-6">
              <div className="text-warning text-base font-semibold mb-4">
                Buy In:
              </div>

              <div className="flex gap-3 flex-wrap">
                {[
                  { label: "≤ ₹100", value: "<=100" },
                  { label: "₹101 - ₹500", value: "101-500" },
                  { label: "₹501 - ₹1000", value: "501-1000" },
                  { label: "₹1001 - ₹5000", value: "1001-5000" },
                  { label: "> ₹5000", value: ">5000" },
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() =>
                      handleMultipleSelection(value, buyIn, setBuyIn)
                    }
                    className={`rounded-300 px-4 py-2 text-sm transition-all duration-300 ease-in-out mb-2 ${
                      buyIn.includes(value)
                        ? "bg-filter-active shadow-[2px_2px_15px] shadow-filter-shadow text-light border-1 border-b-sub"
                        : "bg-transparent text-secondary border-1 border-b-secondary"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Format Section */}
            <div className="mb-6">
              <div className="text-warning text-base font-semibold mb-4">
                Format:
              </div>

              <div className="flex gap-3 flex-wrap">
                {[
                  { label: "Re-Entry", value: "ReEntry" },
                  { label: "Rebuy", value: "RebuyWithoutAddOn" },
                  { label: "Knockout", value: "Knockout" },
                  { label: "Mystery Bounty", value: "MysteryBounty" },
                  { label: "Deal Making", value: "DealMaking" },
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() =>
                      handleMultipleSelection(value, format, setFormat)
                    }
                    className={`rounded-300 px-4 py-2 text-sm transition-all duration-300 ease-in-out mb-2 ${
                      format.includes(value)
                        ? "bg-filter-active shadow-[2px_2px_15px] shadow-filter-shadow text-light border-1 border-b-sub"
                        : "bg-transparent text-secondary border-1 border-b-secondary"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-10 p-4 gap-2 border-t-2 border-b-main">
            <div
              onClick={resetFilters}
              disabled={!hasFiltersSelected}
              className={`col-span-3 pt-3 text-center text-base font-semibold transition-all duration-300 ease-in-out ${
                hasFiltersSelected
                  ? "text-success border-b-2 border-b-success"
                  : "text-secondary border-b-2 border-b-secondary"
              }`}
            >
              Reset Filters
            </div>

            <DrawerClose
              role="button"
              onClick={applyFilters}
              disabled={!shouldEnableApply}
              className={`col-span-7 text-center border-border-main rounded-200 p-2.5 text-base font-semibold transition-all duration-300 ease-in-out ${
                shouldEnableApply
                  ? "bg-linear-to-b from-button-1 to-button-2 text-light opacity-100"
                  : "bg-linear-to-b from-card-grad-grey-400 to-card-grad-grey-700 text-primary opacity-70"
              }`}
            >
              <div
                role="button"
                onClick={applyFilters}
                disabled={!shouldEnableApply}
              >
                Apply
              </div>
            </DrawerClose>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default A23TourneyGamesSort;
